import { FileTextOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  Row,
  Tag,
  Tooltip,
} from 'antd';
import { getParcialidades } from 'api/recaudacion/convenios';
import Table from 'components/Table';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { DATE_FRONTEND_FORMAT } from 'utils/formatValues';
import currencyFormatter from 'utils/toCurrency';
import Moment from 'moment';

/**
 * Convierte un importe a centavos
 * @param {number} amount - El importe a convertir
 * @returns {number} - El importe en centavos
 */
const toCents = (amount) => Math.round(amount * 100);

/**
 * Convierte un importe en centavos a importe
 * @param {number} cents - El importe en centavos
 * @returns {number} - El importe
 */
const fromCents = (cents) => cents / 100;

const parcialidadesMapper = (p) => ({
  ...p,
  sobrante: 0,
  _importe_de_la_parcialidad: -toCents(p.importe_de_la_parcialidad),
  importe_de_la_parcialidad: toCents(p.importe_de_la_parcialidad),
  adeudo: toCents(p.adeudo),
  importe_original: toCents(p.importe_original),
});

const SectionCobros = ({
  convenio,
  setLoading,
  callbackReporte,
}) => {
  const [data, setData] = useState([]);

  const columns = [
    {
      titleText: 'Mensualidad',
      dataIndex: 'mensualidad',
      key: 'mensualidad',
      width: 100,
      align: 'center',
      render: (value, record) => <MensualidadRenderer value={value} record={record} />,
    },
    {
      titleText: 'Fecha de vencimiento',
      dataIndex: 'fecha_de_vencimiento',
      key: 'fecha_de_vencimiento',
      width: 100,
      align: 'center',
      render: (value, record) => <FechaVencimientoRenderer value={value} record={record} />,
    },
    {
      titleText: 'Importe',
      dataIndex: 'adeudo',
      key: 'adeudo',
      align: 'right',
      width: 100,
      render: (value, record) => <ImporteRenderer value={value} record={record} />,
    },
  ];

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      setLoading(true);
      const _data = await getParcialidades({ convenio });
      if (mounted) {
        setData(_data.map(parcialidadesMapper));
        setLoading(false);
      }
    };
    if (convenio) {
      fetchData();
    }
    return () => { mounted = false; };
  }, [convenio]);

  return (
    <Row gutter={20}>
      <Col xs={24} sm={24} md={12}>
        <Table
          data={data}
          cols={columns}
          noControls
          rowKey="id"
          allowSort={false}
          mobileColIndex={2}
          pageSize={data.length}
        />
      </Col>
      <Col xs={24} sm={24} md={12}>
        <Row justify="center" align="middle" style={{ height: '100%' }}>
          <Col>
            <Button
              type="primary"
              icon={<FileTextOutlined />}
              size="large"
              onClick={callbackReporte}
            >
              Orden de pago
            </Button>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

SectionCobros.propTypes = {
  setLoading: PropTypes.func.isRequired,
  convenio: PropTypes.number,
  callbackReporte: PropTypes.func.isRequired,
};

SectionCobros.defaultProps = {
  convenio: 0,
};

// Componente para renderizar mensualidad
const MensualidadRenderer = ({ value, record }) => {
  if (value === 0) {
    return (
      <Tag color="blue" style={{ margin: 0 }}>
        Anticipo
      </Tag>
    );
  }

  // Agregar indicador visual si está saldado
  if (record?.saldado) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span>{value}</span>
        <Tag color="success" size="small">
          ✓
        </Tag>
      </div>
    );
  }

  return <span>{value}</span>;
};

MensualidadRenderer.propTypes = {
  value: PropTypes.number.isRequired,
  record: PropTypes.shape({
    saldado: PropTypes.bool,
  }),
};

MensualidadRenderer.defaultProps = {
  record: {},
};

// Componente especializado para fechas de vencimiento
const FechaVencimientoRenderer = ({ value, record }) => {
  if (!value) {
    return <span style={{ color: '#999' }}>-</span>;
  }

  const today = Moment();
  const vencimiento = Moment(value);
  const diasDiferencia = vencimiento.diff(today, 'days');

  let color = 'default';
  let statusText = '';

  if (record.saldado) {
    color = 'success';
    statusText = ' (Pagado)';
  } else if (diasDiferencia < 0) {
    color = 'error';
    statusText = ` (Vencido hace ${Math.abs(diasDiferencia)} días)`;
  } else if (diasDiferencia <= 3) {
    color = 'warning';
    statusText = ` (Vence en ${diasDiferencia} días)`;
  }

  const formattedDate = value.format(DATE_FRONTEND_FORMAT);

  return (
    <Tooltip title={`Fecha completa: ${value.format('DD/MM/YYYY')}${statusText}`}>
      <Tag color={color} style={{ margin: 0 }}>
        {formattedDate}
      </Tag>
    </Tooltip>
  );
};

FechaVencimientoRenderer.propTypes = {
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.instanceOf(Moment)]),
  record: PropTypes.shape({
    saldado: PropTypes.bool,
  }).isRequired,
};

FechaVencimientoRenderer.defaultProps = {
  value: null,
};

// Componente para mostrar importe con descuentos
const ImporteConDescuento = ({ record, value }) => {
  const diff = record.importe_original - record.importe_de_la_parcialidad;
  return (
    <>
      <Tag color="blue" style={{ margin: 0 }}>
        <Tooltip title="Importe original">
          {currencyFormatter(record.importe_original)}
        </Tooltip>
      </Tag>
      {' - '}
      <Tag color="red" style={{ margin: 0 }}>
        <Tooltip title="Importe abonado">
          {currencyFormatter(diff)}
        </Tooltip>
      </Tag>
      {' = '}
      {currencyFormatter(value)}
    </>
  );
};

ImporteConDescuento.propTypes = {
  record: PropTypes.shape({
    importe_original: PropTypes.number,
    importe_de_la_parcialidad: PropTypes.number,
  }).isRequired,
  value: PropTypes.number.isRequired,
};

// Componente para mostrar importe saldado
const ImporteSaldado = ({ record }) => (
  <>
    <Tag color="success">
      Abonado:
      {' '}
      {currencyFormatter(record.total_abonado)}
    </Tag>
    {' '}
    <span>
      {currencyFormatter(0)}
    </span>
  </>
);

ImporteSaldado.propTypes = {
  record: PropTypes.shape({
    total_abonado: PropTypes.number,
  }).isRequired,
};

// Componente para mostrar importe con sobrante
const ImporteConSobrante = ({ record, value }) => {
  const _sobrante = record.sobrante;
  const _result = (value - _sobrante).toFixed(2);

  if (_result === '0.00') {
    return currencyFormatter(fromCents(value));
  }

  return (
    <>
      {currencyFormatter(fromCents(value))}
      <Tooltip title="Abono a la próxima mensualidad con el sobrante.">
        <span className="danger">
          {' - '}
          {currencyFormatter(fromCents(_sobrante))}
        </span>
      </Tooltip>
      <span className="success">
        {' = '}
        {currencyFormatter(fromCents(_result))}
      </span>
    </>
  );
};

ImporteConSobrante.propTypes = {
  record: PropTypes.shape({
    sobrante: PropTypes.number,
  }).isRequired,
  value: PropTypes.number.isRequired,
};

// Componente para mostrar importe con abono parcial
const ImporteConAbonoParcial = ({ record, value }) => (
  <>
    {(record?.total_abonado > 0 && !record?.saldado) && (
      <Tag color="success">
        Abonado:
        {' '}
        {currencyFormatter(record.total_abonado)}
      </Tag>
    )}
    <Tooltip title={`Importe original (${currencyFormatter(fromCents(record.importe_original))})`}>
      {currencyFormatter(fromCents(value))}
    </Tooltip>
  </>
);

ImporteConAbonoParcial.propTypes = {
  record: PropTypes.shape({
    total_abonado: PropTypes.number,
    saldado: PropTypes.bool,
    importe_original: PropTypes.number,
  }).isRequired,
  value: PropTypes.number.isRequired,
};

// Componente principal para renderizar importe
const ImporteRenderer = ({ value, record }) => {
  const _value = (value * 100) / 100;

  // Caso 1: Importe con descuento (importe original diferente al de la parcialidad)
  if (record?.importe_original !== record?.importe_de_la_parcialidad) {
    return <ImporteConDescuento record={record} value={_value} />;
  }

  // Caso 2: Parcialidad saldada
  if (record.saldado) {
    return <ImporteSaldado record={record} />;
  }

  // Caso 3: Importe con sobrante
  if (record?.sobrante) {
    return <ImporteConSobrante record={record} value={_value} />;
  }

  // Caso 4: Importe normal con abono parcial
  return <ImporteConAbonoParcial record={record} value={_value} />;
};

ImporteRenderer.propTypes = {
  value: PropTypes.number.isRequired,
  record: PropTypes.shape({
    importe_original: PropTypes.number,
    importe_de_la_parcialidad: PropTypes.number,
    saldado: PropTypes.bool,
    sobrante: PropTypes.number,
  }).isRequired,
};

export default SectionCobros;
